import React, { useState, useEffect } from 'react';
import { publishService } from '../../services/publishService';
import { useTemplateStore, type TemplateType } from '../../stores/templateStore';
import type { PublishedPageInfo } from '../../services/publishService';

interface PagesManagerProps {
  isOpen: boolean;
  onClose: () => void;
}

// 模板图标映射
const templateIcons: Record<TemplateType, string> = {
  'edu-course': '🎓',
  'product-showcase': '📦',
  'event-promotion': '📅',
  'company-intro': '🏢',
  'resource-download': '📥',
};

// 模板名称映射
const templateNames: Record<TemplateType, string> = {
  'edu-course': '教育课程',
  'product-showcase': '产品展示',
  'event-promotion': '活动宣传',
  'company-intro': '企业介绍',
  'resource-download': '资源下载',
};

export const PagesManager: React.FC<PagesManagerProps> = ({
  isOpen,
  onClose,
}) => {
  const { publishedPages, deletePublishedPage } = useTemplateStore();
  const [pages, setPages] = useState<PublishedPageInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [shouldLoad, setShouldLoad] = useState(false);

  // 触发加载
  useEffect(() => {
    if (isOpen) {
      setShouldLoad(true);
    }
  }, [isOpen]);

  // 加载页面列表
  useEffect(() => {
    if (!shouldLoad || !isOpen) return;

    let cancelled = false;

    const loadPages = async () => {
      setIsLoading(true);

      try {
        const result = await publishService.getPages();
        if (!cancelled) {
          if (result.success && result.data) {
            setPages(result.data.pages);
          } else {
            // 使用本地存储的数据
            const localPages: PublishedPageInfo[] = publishedPages.map((p) => ({
              id: p.id,
              title: p.title,
              path: p.path,
              url: p.url,
              templateId: p.templateId,
              publishedAt: new Date(p.publishedAt).toISOString(),
              updatedAt: new Date(p.updatedAt).toISOString(),
              views: 0,
            }));
            setPages(localPages);
          }
        }
      } catch (error) {
        console.error('Load pages error:', error);
        if (!cancelled) {
          // 使用本地存储的数据
          const localPages: PublishedPageInfo[] = publishedPages.map((p) => ({
            id: p.id,
            title: p.title,
            path: p.path,
            url: p.url,
            templateId: p.templateId,
            publishedAt: new Date(p.publishedAt).toISOString(),
            updatedAt: new Date(p.updatedAt).toISOString(),
            views: 0,
          }));
          setPages(localPages);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setShouldLoad(false);
        }
      }
    };

    loadPages();

    return () => {
      cancelled = true;
    };
  }, [shouldLoad, isOpen, publishedPages]);

  // 删除页面
  const handleDelete = async (page: PublishedPageInfo) => {
    if (!window.confirm(`确定要删除页面 "${page.title}" 吗？`)) {
      return;
    }

    setDeletingId(page.id);

    try {
      const result = await publishService.deletePage(page.path);
      if (result.success) {
        deletePublishedPage(page.id);
        setPages((prev) => prev.filter((p) => p.id !== page.id));
      } else {
        alert(`删除失败: ${result.error}`);
      }
    } catch (error) {
      console.error('Delete error:', error);
      // 本地删除
      deletePublishedPage(page.id);
      setPages((prev) => prev.filter((p) => p.id !== page.id));
    } finally {
      setDeletingId(null);
    }
  };

  // 复制链接
  const handleCopyLink = async (page: PublishedPageInfo) => {
    try {
      await navigator.clipboard.writeText(page.url);
      setCopiedId(page.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      // 降级方案
      const input = document.createElement('input');
      input.value = page.url;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopiedId(page.id);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  // 预览页面
  const handlePreview = (page: PublishedPageInfo) => {
    window.open(page.url, '_blank');
  };

  // 格式化日期
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[80vh] flex flex-col">
        {/* 头部 */}
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-gray-900">页面管理</h3>
            <p className="text-sm text-gray-500 mt-1">
              共 {pages.length} 个已发布页面
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 内容 */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <svg className="animate-spin h-8 w-8 text-purple-500" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            </div>
          ) : pages.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <h4 className="text-lg font-medium text-gray-900 mb-2">暂无已发布页面</h4>
              <p className="text-gray-500">发布你的第一个页面，它将显示在这里</p>
            </div>
          ) : (
            <div className="space-y-4">
              {pages.map((page) => (
                <div
                  key={page.id}
                  className="bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-start gap-4">
                    {/* 模板图标 */}
                    <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-2xl shadow-sm">
                      {templateIcons[page.templateId]}
                    </div>

                    {/* 页面信息 */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-gray-900 truncate">
                          {page.title}
                        </h4>
                        <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-600 rounded-full">
                          {templateNames[page.templateId]}
                        </span>
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        /{page.path}/
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                        <span>发布于 {formatDate(page.publishedAt)}</span>
                        {page.views !== undefined && (
                          <span className="flex items-center gap-1">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            {page.views} 次浏览
                          </span>
                        )}
                      </div>
                    </div>

                    {/* 操作按钮 */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handlePreview(page)}
                        className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                        title="预览"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleCopyLink(page)}
                        className={`p-2 rounded-lg transition-colors ${
                          copiedId === page.id
                            ? 'text-green-600 bg-green-50'
                            : 'text-gray-500 hover:text-purple-600 hover:bg-purple-50'
                        }`}
                        title={copiedId === page.id ? '已复制' : '复制链接'}
                      >
                        {copiedId === page.id ? (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        )}
                      </button>
                      <button
                        onClick={() => handleDelete(page)}
                        disabled={deletingId === page.id}
                        className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                        title="删除"
                      >
                        {deletingId === page.id ? (
                          <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 底部 */}
        <div className="px-6 py-4 border-t bg-gray-50 rounded-b-2xl">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  );
};
